import { createContext, useCallback, useContext, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { API_BASE } from "../lib/links";

const PhoneCtx = createContext(null);

export const PhoneCaptureProvider = ({ children }) => {
  const { setUser } = useAuth();
  const [pending, setPending] = useState(null); // { resolve }
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Resolves with the 10-digit phone, "" if skipped, null if closed without choosing
  const ensurePhone = useCallback((user) => {
    if (user?.phone) return Promise.resolve(user.phone);
    setPhone("");
    setError("");
    return new Promise((resolve) => setPending({ resolve }));
  }, []);

  const close = (value) => {
    pending?.resolve(value);
    setPending(null);
  };

  const save = async () => {
    const cleaned = phone.replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE}/auth/phone`, { phone: cleaned }, { withCredentials: true });
      setUser(res.data);
      close(cleaned);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PhoneCtx.Provider value={ensurePhone}>
      {children}
      {pending && (
        <div
          data-testid="phone-capture-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 px-5 backdrop-blur-sm"
        >
          <div className="w-full max-w-md rounded-2xl border border-paper/15 bg-ink p-8 text-paper shadow-2xl">
            <p className="font-mono-accent text-xs font-semibold uppercase tracking-[0.2em] text-ember">
              One quick thing
            </p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight">
              Your mobile number
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-paper/60">
              Razorpay uses it for payment OTPs and receipts — saved once, so checkout is one tap
              every time after.
            </p>
            <label htmlFor="phone-capture-input" className="sr-only">
              Mobile number
            </label>
            <div className="mt-6 flex items-center gap-2 rounded-full border border-paper/20 bg-paper/5 px-5 py-3 focus-within:border-ember">
              <span className="font-mono-accent text-sm text-paper/50">+91</span>
              <input
                id="phone-capture-input"
                data-testid="phone-input"
                type="tel"
                inputMode="numeric"
                autoFocus
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => e.key === "Enter" && save()}
                placeholder="98765 43210"
                className="w-full bg-transparent font-mono-accent text-sm tracking-wider text-paper placeholder:text-paper/30 focus:outline-none"
              />
            </div>
            {error && (
              <p data-testid="phone-error" className="mt-3 text-sm text-ember">
                {error}
              </p>
            )}
            <button
              onClick={save}
              disabled={saving}
              data-testid="phone-save-button"
              className="mt-6 w-full rounded-full bg-ember px-6 py-3.5 text-base font-semibold text-paper transition-all duration-200 hover:bg-[#c94f22] disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save & continue to payment"}
            </button>
            <button
              onClick={() => close("")}
              data-testid="phone-skip-button"
              className="mt-3 w-full rounded-full px-6 py-2.5 text-sm font-medium text-paper/50 transition-colors duration-200 hover:text-paper"
            >
              Skip for now
            </button>
          </div>
        </div>
      )}
    </PhoneCtx.Provider>
  );
};

export const usePhoneCapture = () => useContext(PhoneCtx);
